import { useEffect, useState } from 'react';
import {
  categoryIdByName,
  mergeCategories,
  mergeWithSnapshot,
  shouldUseSnapshot,
  snapshot,
} from '../lib/cmsFallback';
import { getPublicImageUrl, supabase } from '../lib/supabase';

function asRelation(value) {
  return Array.isArray(value) ? value[0] || null : value;
}

function asList(value) {
  return (value || '')
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/^[-*\u2022]\s*/, ''))
    .filter(Boolean);
}

export default function usePortfolio() {
  const [categories, setCategories] = useState(snapshot.portfolioCategories);
  const [projects, setProjects] = useState(snapshot.projects);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadPortfolio() {
      try {
      const [categoriesResult, projectsResult] = await Promise.all([
        supabase
          .from('portfolio_categories')
          .select('id, name, created_at')
          .order('name', { ascending: true }),
        supabase
          .from('portfolios')
          .select(`
            id, title, description, main_image_path, client, location, area,
            category_id, challenges, solutions, ending_image_path, created_at,
            category:portfolio_categories(id, name),
            images:portfolio_images(id, image_path, position)
          `)
          .order('created_at', { ascending: false }),
      ]);

      if (!active) return;

      if (shouldUseSnapshot(categoriesResult) || shouldUseSnapshot(projectsResult)) {
        setCategories(snapshot.portfolioCategories);
        setProjects(snapshot.projects);
        setLoading(false);
        return;
      }

      if (categoriesResult.error) {
        console.error('Could not load portfolio categories:', categoriesResult.error.message);
      }

      const mergedCategories = mergeCategories(
        categoriesResult.error ? [] : categoriesResult.data,
        snapshot.portfolioCategories,
      );
      setCategories(mergedCategories);

      if (projectsResult.error) {
        console.error('Could not load portfolio projects:', projectsResult.error.message);
        setProjects(snapshot.projects);
        setLoading(false);
        return;
      }

      const remoteProjects = (projectsResult.data || []).map((project) => {
        const category = asRelation(project.category);
        const gallery = [...(project.images || [])].sort(
          (a, b) => a.position - b.position,
        );

        return {
          id: project.id,
          title: project.title,
          sub1: category?.name || '',
          sub2: '',
          desc: project.description,
          img: getPublicImageUrl('portfolio-images', project.main_image_path),
          client: project.client,
          location: project.location,
          area: project.area,
          categoryId: categoryIdByName(
            mergedCategories,
            category?.name,
            project.category_id,
          ),
          images: gallery.map((image) =>
            getPublicImageUrl('portfolio-images', image.image_path),
          ),
          challenges: asList(project.challenges),
          solutions: asList(project.solutions),
          endingImage: getPublicImageUrl(
            'portfolio-images',
            project.ending_image_path,
          ),
        };
      });

      setProjects(
        mergeWithSnapshot(remoteProjects, snapshot.projects, { remoteFirst: true }),
      );
      setLoading(false);
      } catch (error) {
        if (!active) return;
        console.error('Could not load portfolio projects:', error);
        setCategories(snapshot.portfolioCategories);
        setProjects(snapshot.projects);
        setLoading(false);
      }
    }

    loadPortfolio();

    return () => {
      active = false;
    };
  }, []);

  return { categories, projects, loading };
}
